import { useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, Link as RouterLink } from 'react-router-dom';

import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  List,
  Stack,
  AppBar,
  Button,
  Drawer,
  Toolbar,
  ListItem,
  Container,
  IconButton,
  Typography,
  ListItemText,
  useMediaQuery,
  ListItemButton,
} from '@mui/material';

import Iconify from '../../components/iconify';
import Logo from '../../components/logo';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  justifyContent: 'space-between',
  padding: theme.spacing(0, 2),
}));

const StyledLogo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  textDecoration: 'none',
  color: 'inherit',
}));

const StyledNavButton = styled(Button)(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: active ? 600 : 400,
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
  },
}));

const navigationItems = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'Support Portal', path: '/support/login' },
];

export default function PublicLayout({ children }) {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Create a new Date object (defaults to the current date and time)
  const today = new Date();

  // Get the full year from the Date object
  const currentYear = today.getFullYear();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        ourTHINGZ
      </Typography>
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{ textAlign: 'center' }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/login" sx={{ textAlign: 'center' }}>
            <ListItemText primary="Sign In" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/signup" sx={{ textAlign: 'center' }}>
            <ListItemText primary="Get Started" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <StyledAppBar position="static">
        <StyledToolbar>
          <StyledLogo component={RouterLink} to="/">
            {/* <Iconify icon="eva:layers-outline" width={32} height={32} color="primary.main" />
            < */}
            <Logo width={60} />
          </StyledLogo>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <Iconify icon="eva:menu-outline" />
            </IconButton>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              {navigationItems.map((item) => (
                <StyledNavButton
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  active={location.pathname === item.path ? 1 : 0}
                >
                  {item.label}
                </StyledNavButton>
              ))}

              <Button component={RouterLink} to="/login" variant="text" sx={{ ml: 2 }}>
                Sign In
              </Button>

              <Button component={RouterLink} to="/signup" variant="contained" sx={{ ml: 1 }}>
                Get Started
              </Button>
            </Stack>
          )}
        </StyledToolbar>
      </StyledAppBar>

      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: (muiTheme) =>
            muiTheme.palette.mode === 'light'
              ? muiTheme.palette.grey[200]
              : muiTheme.palette.grey[800],
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © 2024 - {currentYear} ourTHINGZ. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                component={RouterLink}
                to="/privacy"
                variant="text"
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                Privacy Policy
              </Button>
              <Button
                component={RouterLink}
                to="/terms"
                variant="text"
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                Terms of Service
              </Button>
              <Button
                component={RouterLink}
                to="/contact"
                variant="text"
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

PublicLayout.propTypes = {
  children: PropTypes.node.isRequired,
};
